import classNames from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { signOut, useSession } from 'next-auth/react';
import styles from './HeaderGlobal.module.scss';
import {
	Logo,
	SkipToMain,
	ServiceNavigation,
	Container,
	Grid,
	GridCell,
	SoMeLink,
	SoMeLinkProps,
	Link,
	Icon,
	SearchCludo,
	LanguageBar,
	PortalList,
	LinkButton,
	Accordion,
} from 'ui/components';
import IconLogin from 'ui/icons/icon-global-login.svg';
import IconLogout from 'ui/icons/icon-global-logout.svg';
import IconLoop from 'ui/icons/icon-global-search.svg';
import IconProfile from 'ui/icons/icon-global-profile.svg';
import { Profiles } from 'application/repositories/profilesRepository';
import { useIsDesktop } from 'application/hooks/useIsDesktop';
import { NavigationGlobal } from 'ui/components/3-organisms/NavigationGlobal/NavigationGlobal';

export interface HeaderGlobalProps {
	className?: string;
	navHeading?: string;
	profiles: Profiles;
	serviceNavigationItems?: Models.ServiceNavigation;
	navigationItems?: Models.NavigationItem[];
	navGroups?: Models.NavGroupItem[];
	navSoMeLinks?: SoMeLinkProps[];
	userTools?: Models.UserTools;
	languageBarTitle?: Models.LanguageBar;
	languageBarItems?: Models.LanguageBar;
	portalList?: Umbraco.PortalList['content']['properties'];
	searchPageUrl?: Umbraco.Link[];
	handleOpenSearch?: () => void;
	portalStateLabel?: string;
	signUpLabel?: string;
	searchLabel?: string;
	navigationMenuLabel?: string;
}

export type navState = boolean;

export const HeaderGlobal: React.FC<HeaderGlobalProps> = ({
	className,
	navHeading,
	navGroups,
	navigationItems,
	profiles,
	serviceNavigationItems,
	navSoMeLinks,
	languageBarItems,
	languageBarTitle,
	portalList,
	userTools,
	searchPageUrl,
	portalStateLabel,
	signUpLabel,
	searchLabel,
	navigationMenuLabel,
}) => {
	const [navOpen, setNavOpen] = useState<navState>(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const isDesktop = useIsDesktop();
	const headerRef = useRef(null);
	const checkboxRef = useRef(null);
	const navRef = useRef(null);

	const handleKeyboardNav = (keyDownEvent) => {
		keyDownEvent.preventDefault();
		if (keyDownEvent.keyCode === 32) {
			if (!checkboxRef.current.checked) {
				checkboxRef.current.checked = true;
				setNavOpen(true);
			} else {
				checkboxRef.current.checked = false;
				setNavOpen(false);
			}
		}
	};

	const { status } = useSession();
	const [isLoggedIn, setIsLoggedIn] = useState(false);

	const searchClick = () => {
		setSearchOpen(!searchOpen);
	};
	const handleClickClose = (event) => {
		const element = headerRef.current;
		if (!element?.contains(event.target)) {
			setSearchOpen(false);
		}
	};
	const handleCloseSearch = () => {
		setSearchOpen(false);
	};

	const handleClickOutsideNav = (event) => {
		if (navRef.current && !navRef.current.contains(event.target)) {
			setNavOpen(false);
			if (checkboxRef.current) checkboxRef.current.checked = false;
		}
	};

	useEffect(() => {
		if (searchOpen) {
			document.addEventListener('click', handleClickClose);
		}
		return () => document.removeEventListener('click', handleClickClose);
	}, [searchOpen]);

	useEffect(() => {
		if (navOpen) {
			document.addEventListener('click', handleClickOutsideNav);
		}
		return () => document.removeEventListener('click', handleClickOutsideNav);
	}, [navOpen]);

	useEffect(() => {
		if (!isDesktop) {
			document.body.style.overflow = navOpen ? 'hidden' : '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [navOpen]);

	useEffect(() => {
		if (status === 'authenticated') {
			setIsLoggedIn(true);
		} else {
			setIsLoggedIn(false);
		}
	}, [status]);

	const signOutClick = () => {
		signOut({
			redirect: false,
		}).then(() => {
			const redirectUrl = userTools?.signOutLink?.url ?? window.location.href;
			window.location.href = `https://${process.env.NEXT_PUBLIC_AUTHENTICATION_DOMAIN}/account/logout?returnUrl=${redirectUrl}`;
		});
	};
	return (
		<header
			ref={headerRef}
			className={classNames(styles.HeaderGlobal, navOpen && styles.HeaderGlobal___active)}
			role="banner"
			aria-expanded={searchOpen}
		>
			<SkipToMain link="#main" title="Click to skip navigations and go directly to content section">
				Skip to content
			</SkipToMain>
			<SearchCludo handleCloseSearch={handleCloseSearch} theme="global" />
			<Container width="Full width" className={styles.HeaderGlobal__container}>
				<Grid nogutter={true} center={true} spaceBetween={true}>
					<Logo className={styles.HeaderGlobal__logo} withText={true} />
					<div className={styles.HeaderGlobal__serviceItemList}>
						{portalList && (
							<div className={classNames(styles.HeaderGlobal__portalList, styles.HeaderGlobal__hideOnMobile)}>
								<span className={styles.HeaderGlobal__portalListTitle}>{portalStateLabel}</span>
								<div className={classNames(styles.HeaderGlobal__serviceItem)}>
									<PortalList items={portalList.portalItems} title={portalList?.portalTitle} isLoggedIn={isLoggedIn} />
								</div>
							</div>
						)}
						{languageBarItems && (
							<div className={classNames(styles.HeaderGlobal__languageBar, styles.HeaderGlobal__hideOnMobile)}>
								<div className={classNames(styles.HeaderGlobal__serviceItem)}>
									<LanguageBar languageBarItems={languageBarItems} languageBarTitle={languageBarTitle} />
								</div>
							</div>
						)}
						{searchPageUrl && (
							<div className={classNames(styles.HeaderGlobal__SearchBar)}>
								<div
									className={classNames(styles.HeaderGlobal__serviceItem, styles.HeaderGlobal__iconWithLabelItem)}
									onClick={searchClick}
								>
									<Link className={styles.HeaderGlobal__link}>
										<Icon className={styles.HeaderGlobal__iconLink}>
											<IconLoop />
										</Icon>
										<span className={styles.HeaderGlobal__linkLabel}>{searchLabel}</span>
									</Link>
								</div>
							</div>
						)}
						{isLoggedIn
							? userTools?.userPageLink?.url && (
									<div
										className={classNames(
											styles.HeaderGlobal__serviceItem,
											styles.HeaderGlobal__filled,
											styles.HeaderGlobal__iconItem,
										)}
									>
										<Link className={styles.HeaderGlobal__link} url={userTools?.userPageLink?.url}>
											<Icon className={classNames(styles.HeaderGlobal__iconLink)}>
												<IconProfile />
											</Icon>
										</Link>
									</div>
							  )
							: userTools?.signUpLink?.url && (
									<div className={classNames(styles.HeaderGlobal__hideOnMobile)}>
										<div className={classNames(styles.HeaderGlobal__serviceItem, styles.HeaderGlobal__filled)}>
											<Link
												className={classNames(styles.HeaderGlobal__memberLink, styles.HeaderGlobal__link)}
												target="blank"
												url={userTools.signUpLink.url}
											>
												<span>{signUpLabel}</span>
											</Link>
										</div>
									</div>
							  )}
						{isLoggedIn
							? userTools?.signOutLink?.url && (
									<div
										className={classNames(
											styles.HeaderGlobal__serviceItem,
											styles.HeaderGlobal__filled,
											styles.HeaderGlobal__iconItem,
										)}
									>
										<Link className={styles.HeaderGlobal__link} onClick={signOutClick}>
											<Icon className={classNames(styles.HeaderGlobal__iconLink, styles.HeaderGlobal__iconLogout)}>
												<IconLogout />
											</Icon>
										</Link>
									</div>
							  )
							: userTools?.signInLink?.url && (
									<div
										className={classNames(
											styles.HeaderGlobal__serviceItem,
											styles.HeaderGlobal__filled,
											styles.HeaderGlobal__iconItem,
										)}
									>
										<Link className={styles.HeaderGlobal__link} url={userTools?.signInLink?.url}>
											<Icon className={classNames(styles.HeaderGlobal__iconLink)}>
												<IconLogin />
											</Icon>
										</Link>
									</div>
							  )}
						{navGroups && (
							<>
								<div ref={navRef} className={classNames(styles.HeaderGlobal__navigation)}>
									<input
										onChange={(event) => (event.target.checked ? setNavOpen(true) : setNavOpen(false))}
										className={styles.HeaderGlobal__navController}
										type="checkbox"
										id="nav-controller"
										data-ref="HeaderGlobal__navController"
										ref={checkboxRef}
									/>
									<label className={styles.HeaderGlobal__navToggle} htmlFor="nav-controller">
										<span className={styles.HeaderGlobal__navToggleText}>{navigationMenuLabel}</span>
										<span
											className={styles.HeaderGlobal__navToggleIcon}
											role="button"
											tabIndex={0}
											aria-expanded={navOpen}
											aria-label="Toggle navigation menu"
											onKeyDown={(e) => handleKeyboardNav(e)}
										>
											<span className={styles.HeaderGlobal__navToggleIconBars}></span>
										</span>
									</label>
									<div
										className={classNames(styles.HeaderGlobal__nav, navOpen && styles.HeaderGlobal__nav__active)}
										data-ref="HeaderGlobal__navPanel"
									>
										<div className={styles.HeaderGlobal__navContent}>
											{(languageBarItems || portalList || userTools?.signUpLink?.url) && (
												<div
													className={classNames(
														styles.HeaderGlobal__navigationToolsContent,
														styles.HeaderGlobal__hideOnDesktop,
													)}
												>
													<div className={styles.HeaderGlobal__navigationTools}>
														{languageBarItems && (
															<div
																className={classNames(
																	styles.HeaderGlobal__languageBar,
																	styles.HeaderGlobal__serviceItem,
																	styles.HeaderGlobal__hideOnDesktop,
																	styles.HeaderGlobal__fullwidthItem,
																)}
															>
																<LanguageBar
																	languageBarItems={languageBarItems}
																	languageBarTitle={languageBarTitle}
																/>
															</div>
														)}
														{!isLoggedIn && userTools?.signUpLink?.url && (
															<div
																className={classNames(
																	styles.HeaderGlobal__serviceItem,
																	styles.HeaderGlobal__filled,
																	styles.HeaderGlobal__hideOnDesktop,
																	styles.HeaderGlobal__fullwidthItem,
																)}
															>
																<Link
																	className={classNames(styles.HeaderGlobal__memberLink, styles.HeaderGlobal__link)}
																	target="blank"
																	url={userTools.signUpLink.url}
																>
																	<span>{signUpLabel}</span>
																</Link>
															</div>
														)}
													</div>
													{portalList && (
														<div
															className={classNames(
																styles.HeaderGlobal__portalList,
																styles.HeaderGlobal__hideOnDesktop,
																styles.HeaderGlobal__fullwidthItem,
															)}
														>
															<div className={styles.HeaderGlobal__portalListTitle}>
																<span>{portalStateLabel}</span>
															</div>
															<div
																className={classNames(
																	styles.HeaderGlobal__serviceItem,
																	styles.HeaderGlobal__fullwidthItem,
																)}
															>
																<PortalList items={portalList.portalItems} title={portalList?.portalTitle} />
															</div>
														</div>
													)}
												</div>
											)}
											<NavigationGlobal
												className={styles.HeaderGlobal__navigationGlobal}
												heading={navHeading}
												navGroups={navGroups}
											/>
											{serviceNavigationItems && (isLoggedIn || !serviceNavigationItems.isRestricted) && (
												<ServiceNavigation
													className={styles.HeaderGlobal__serviceNavigation}
													serviceNavigationItems={serviceNavigationItems}
												/>
											)}
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				</Grid>
			</Container>
		</header>
	);
};
